import { queryOptions, useQuery } from "@tanstack/react-query";

import { zipLookupApi } from "../../../../../api/client";
import { isHttpError } from "../../../../../lib/query/errors";

const ZIP_PATTERN = /^\d{5}$/;

/**
 * Whether a value is a complete 5-digit ZIP worth querying.
 * @param {string} [zip] - The raw ZIP field value.
 * @returns {boolean} True when the value is exactly five digits.
 */
export function isCompleteZip(zip?: string): zip is string {
  return zip !== undefined && ZIP_PATTERN.test(zip);
}

export const zipLookupKeys = {
  all: ["zipLookup"] as const,
  detail: (zip: string) => [...zipLookupKeys.all, zip] as const,
};

/**
 * Shared queryOptions for a ZIP lookup. ZIP -> city/state is effectively
 * immutable, so it's cached indefinitely; TanStack Query's per-key cache also
 * gives us free de-duplication (the same ZIP is never refetched).
 * @param {string} zip - A complete 5-digit ZIP.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function zipLookupQuery(zip: string) {
  return queryOptions({
    queryKey: zipLookupKeys.detail(zip),
    queryFn: async ({ signal }) => {
      const response = await zipLookupApi.lookupZipCode(zip, { signal });
      return response.data;
    },
    // A successful lookup is immutable and a 404 is a definitive "no match", so
    // both are cached indefinitely. Any other (transient) failure is marked
    // immediately stale so re-entering the same ZIP refetches instead of being
    // stuck on the cached error — matching the "Try again" copy.
    staleTime: (query) =>
      query.state.status === "error" && !isHttpError(query.state.error, 404) ? 0 : Number.POSITIVE_INFINITY,
    // Don't auto-retry: a 404 is a definitive "no match", and for any failure
    // the field shows an immediate "try again" message — auto-retry would only
    // delay that feedback (and waste requests on the deterministic 404). The
    // staleTime above is what makes a transient error retryable on re-entry.
    retry: false,
  });
}

/**
 * Looks up city/state for a ZIP. The query only runs once the value is a
 * complete 5-digit ZIP; partial input keeps it idle.
 * @param {string | undefined} zip - The current ZIP field value.
 * @returns {ReturnType<typeof useQuery>} The TanStack Query result for the ZIP lookup.
 */
export function useZipLookup(zip: string | undefined) {
  const enabled = isCompleteZip(zip);
  return useQuery({
    ...zipLookupQuery(enabled ? zip : ""),
    enabled,
  });
}
